import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  Info,
  Landmark,
  Loader2,
  RefreshCw,
  Wallet,
  X,
  XCircle,
} from "lucide-react";
import {
  clearHouseLoan,
  fetchHouseLoanDesk,
  payHouseLoan,
  requestHouseLoan,
} from "../../redux/operatorConsoleSlice";
import { canUserRunConsoleTreasuryMove } from "../../utils/betHousePartners";
import { formatCash } from "../../utils/operatorConsoleHelpers";
import { formatAppDate } from "../../utils/appTimeFormat";
import {
  buildPercentPicks,
  formatFutureTime,
  LOAN_FEE_RATE,
  loanUiCopy,
  round2,
  SCORE_TARGET,
  tierLabel,
} from "../../utils/loanDeskHelpers";
import LoanCreditGauge from "./LoanCreditGauge";
import "../../operator-finance.css";

/** Platform-wide ceiling: the active band tops out at 10% of treasury. */
const PLATFORM_MAX_PERCENT = 10;

const partnerInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return String(name).slice(0, 2).toUpperCase() || "?";
};

const LoanInfoModal = ({ open, onClose }) => {
  if (!open) return null;

  return createPortal(
    <div className="oc-loan-info-root" role="dialog" aria-modal="true">
      <button type="button" className="oc-loan-info-backdrop" aria-label="Close" onClick={onClose} />
      <div className="oc-loan-info-sheet">
        <header className="oc-loan-info-head">
          <h3>House loan guide</h3>
          <button type="button" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>
        <div className="oc-loan-info-body">
          <section>
            <h4>Who qualifies</h4>
            <ul>
              <li>Only the house creator can request a loan.</li>
              <li>Every accepted partner must meet weekly activity score rules.</li>
              <li>Standard band: 50+ weekly points — borrow 1.5–5% of treasury.</li>
              <li>Active band: 51+ weekly points — borrow 6–10% of treasury.</li>
              <li>No active loan, no loan in the past 7 days, no pending partner invite.</li>
            </ul>
          </section>
          <section>
            <h4>Repayment</h4>
            <ul>
              <li>2.5% fee on borrowed amount, included in total repayment.</li>
              <li>Fee collected from house profits on losing tickets first.</li>
              <li>Creator can pay manually from house balance anytime.</li>
              <li>Auto-sweep after 7 days until cleared.</li>
              <li>Treasury withdrawals blocked until fully repaid.</li>
            </ul>
          </section>
        </div>
        <button type="button" className="oc-loan-info-done" onClick={onClose}>
          Got it
        </button>
      </div>
    </div>,
    document.body
  );
};

const ConfirmModal = ({ open, title, body, confirmLabel, busy, termsNote, onClose, onConfirm }) => {
  if (!open) return null;

  return createPortal(
    <div className="oc-loan-confirm-root" role="dialog" aria-modal="true">
      <button
        type="button"
        className="oc-loan-info-backdrop"
        aria-label="Close"
        onClick={busy ? undefined : onClose}
      />
      <div className="oc-loan-confirm-sheet">
        <h3>{title}</h3>
        <p>{body}</p>
        {termsNote ? <em className="oc-loan-confirm-terms">{termsNote}</em> : null}
        <div className="oc-loan-confirm-foot">
          <button type="button" className="oc-loan-confirm-cancel" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="oc-loan-confirm-go" onClick={onConfirm} disabled={busy}>
            {busy ? <Loader2 size={16} className="oc-treasury-modal-spin" /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const OperatorLoanPanel = ({ bet, operatorUserId, onLoanSuccess }) => {
  const dispatch = useDispatch();
  const betId = bet?._id;

  const {
    houseLoanEligibility: eligibility,
    houseLoanState: loanState,
    houseLoanStatus,
    houseLoanError,
    houseLoanRequesting,
    houseLoanPaying,
  } = useSelector((s) => s.operatorConsole);

  const [percent, setPercent] = useState("");
  const [infoOpen, setInfoOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [toast, setToast] = useState(null);

  const isCreator = canUserRunConsoleTreasuryMove(bet, operatorUserId);

  const reload = useCallback(() => {
    if (!betId || !operatorUserId) return;
    dispatch(fetchHouseLoanDesk({ betId, operatorUserId }));
  }, [betId, dispatch, operatorUserId]);

  useEffect(() => {
    reload();
    return () => dispatch(clearHouseLoan());
  }, [reload, dispatch]);

  useEffect(() => {
    if (!toast) return undefined;
    const id = window.setTimeout(() => setToast(null), 4200);
    return () => window.clearTimeout(id);
  }, [toast]);

  const activeLoan =
    loanState?.activeLoan || eligibility?.activeLoan || null;
  const available = Number(eligibility?.availableBalance ?? bet?.amountAvailable ?? 0);
  const band = eligibility?.band || null;
  const numericPercent = Number(percent);
  const percentOk =
    band &&
    Number.isFinite(numericPercent) &&
    numericPercent >= Number(band.min) &&
    numericPercent <= Number(band.max);

  const loanAmount = percentOk ? round2((available * numericPercent) / 100) : 0;
  const loanFee = round2(loanAmount * LOAN_FEE_RATE);
  const totalToPay = round2(loanAmount + loanFee);

  const remaining = Number(activeLoan?.remainingTotal || 0);
  const remainingPrincipal = Number(activeLoan?.remainingPrincipal || 0);
  const remainingFee = Number(activeLoan?.remainingInterest || 0);
  const payAmount = Math.min(available, remaining);

  const totalDue = activeLoan
    ? Number(activeLoan.totalRepaymentAmount) ||
      round2(Number(activeLoan.loanAmount || 0) + Number(activeLoan.interestAmount || 0))
    : 0;
  const paidSoFar = Math.max(0, totalDue - remaining);
  const repayProgress = totalDue > 0 ? paidSoFar / totalDue : 0;

  const canBorrow = !activeLoan && eligibility?.canRequest;
  const isLocked = !activeLoan && !eligibility?.canRequest;
  const percentPicks = useMemo(() => buildPercentPicks(band), [band]);
  const partners = eligibility?.partners || [];
  const blockers = eligibility?.blockers || [];
  const canRequest =
    isCreator && eligibility?.canRequest && percentOk && loanAmount > 0 && !houseLoanRequesting;

  const statusPill = activeLoan ? "Active" : canBorrow ? "Available" : "Locked";
  const maxCash = band ? formatCash(round2((available * Number(band.max)) / 100), { compact: true }) : "—";

  const runRequest = async () => {
    try {
      await dispatch(
        requestHouseLoan({ betId, operatorUserId, loanPercent: numericPercent })
      ).unwrap();
      setToast({ type: "success", message: `${formatCash(loanAmount, { compact: true })} added to the house.` });
      setPercent("");
      setConfirmModal(null);
      reload();
      onLoanSuccess?.();
    } catch (err) {
      setToast({ type: "error", message: String(err) });
    }
  };

  const runPay = async () => {
    try {
      const result = await dispatch(payHouseLoan({ betId, operatorUserId })).unwrap();
      const applied = result?.data?.applied || 0;
      setToast({
        type: "success",
        message: `${formatCash(applied, { compact: true })} paid toward the loan.`,
      });
      setConfirmModal(null);
      reload();
      onLoanSuccess?.();
    } catch (err) {
      setToast({ type: "error", message: String(err) });
    }
  };

  if (!betId) return null;

  return (
    <section className="oc-panel oc-loan-panel">
      <header className="oc-panel-head oc-loan-head">
        <div>
          <p className="oc-loan-eyebrow">Credit line</p>
          <h2>House loan</h2>
          <p>
            {canBorrow
              ? "Choose an amount within your band, then confirm."
              : activeLoan
              ? "Repay from house balance or wait for auto-sweep."
              : "Borrow against treasury when eligibility requirements are met."}
          </p>
        </div>
        <div className="oc-loan-head-actions">
          <span className={`oc-loan-status oc-loan-status-${statusPill.toLowerCase()}`}>{statusPill}</span>
          <button type="button" className="oc-loan-icon-btn" onClick={() => setInfoOpen(true)} aria-label="Loan guide">
            <Info size={15} />
          </button>
          <button type="button" className="oc-loan-icon-btn" onClick={reload} aria-label="Refresh">
            <RefreshCw size={15} className={houseLoanStatus === "loading" ? "oc-treasury-modal-spin" : ""} />
          </button>
        </div>
      </header>

      {toast ? (
        <div className={`oc-toast oc-toast-${toast.type === "error" ? "error" : "success"}`} role="status">
          {toast.message}
        </div>
      ) : null}

      {houseLoanError ? (
        <div className="oc-alert oc-alert-warning">
          <AlertCircle size={14} />
          <div>
            <strong>Loan error</strong>
            <p>{loanUiCopy(String(houseLoanError))}</p>
          </div>
        </div>
      ) : null}

      {!isCreator ? (
        <div className="oc-alert oc-treasury-readonly">
          <Info size={14} />
          <div>
            <strong>Creator only</strong>
            <p>Only the house creator can request or pay down a house loan.</p>
          </div>
        </div>
      ) : null}

      {houseLoanStatus === "loading" && !eligibility ? (
        <div className="oc-loading oc-loan-loading">
          <Loader2 size={22} className="oc-treasury-modal-spin" />
          <span>Loading credit line…</span>
        </div>
      ) : null}

      {activeLoan ? (
        <div className="oc-loan-card oc-loan-card-active">
          <div className="oc-fin-loan-duo">
            <LoanCreditGauge
              fraction={repayProgress}
              tone="active"
              value={`${Math.round(repayProgress * 100)}%`}
              label="Repaid"
              minLabel="0%"
              maxLabel="100%"
              foot={`${formatCash(paidSoFar, { compact: true })} of ${formatCash(totalDue, {
                compact: true,
              })} cleared`}
            />

            <div>
              <span className="oc-fin-kicker">Outstanding</span>
              <p className="oc-loan-amount tabular-nums">
                {formatCash(remaining, { compact: true })}
              </p>
              <div className="oc-fin-repay">
                <div className="oc-fin-repay-track" aria-hidden>
                  <span
                    className="oc-fin-repay-fill"
                    style={{ width: `${Math.round(repayProgress * 100)}%` }}
                  />
                </div>
                <div className="oc-fin-repay-meta">
                  <span>
                    Principal {formatCash(remainingPrincipal, { compact: true })} · Fee{" "}
                    {formatCash(remainingFee, { compact: true })} left
                  </span>
                </div>
              </div>

              <div className="oc-fin-break" style={{ marginTop: 8 }}>
                <div className="oc-fin-break-row">
                  <span>Borrowed</span>
                  <strong className="tabular-nums">
                    {formatCash(activeLoan.loanAmount, { compact: true })}
                  </strong>
                </div>
                <div className="oc-fin-break-row">
                  <span>Fee ({Number(activeLoan.interestRate) || 2.5}%)</span>
                  <strong className="tabular-nums">
                    {formatCash(activeLoan.interestAmount, { compact: true })}
                  </strong>
                </div>
                <div className="oc-fin-break-row">
                  <span>Paid so far</span>
                  <strong className="tabular-nums oc-fin-up">
                    {formatCash(paidSoFar, { compact: true })}
                  </strong>
                </div>
                <div className="oc-fin-break-row oc-fin-break-total">
                  <span>Still owed</span>
                  <strong className="tabular-nums oc-fin-down">
                    {formatCash(remaining, { compact: true })}
                  </strong>
                </div>
              </div>
            </div>
          </div>

          <div className="oc-fin-dates" style={{ marginTop: 10 }}>
            <div className="oc-fin-date-item">
              <Clock3 size={12} />
              <div>
                <em>Issued</em>
                <strong>{activeLoan.issuedAt ? formatAppDate(activeLoan.issuedAt, { includeWeekday: false }) : "—"}</strong>
              </div>
            </div>
            <div className="oc-fin-date-item">
              <Wallet size={12} />
              <div>
                <em>Last payment</em>
                <strong>
                  {activeLoan.lastPaymentAt
                    ? formatAppDate(activeLoan.lastPaymentAt, { includeWeekday: false })
                    : "None yet"}
                </strong>
              </div>
            </div>
            <div className="oc-fin-date-item">
              <RefreshCw size={12} />
              <div>
                <em>Auto-sweep</em>
                <strong>
                  {activeLoan.sweepEligibleAt
                    ? formatFutureTime(activeLoan.sweepEligibleAt) || "eligible"
                    : "—"}
                </strong>
              </div>
            </div>
            <div className="oc-fin-date-item">
              <Landmark size={12} />
              <div>
                <em>Due</em>
                <strong>
                  {activeLoan.dueDate
                    ? formatAppDate(activeLoan.dueDate, { includeWeekday: false })
                    : "Until cleared"}
                </strong>
              </div>
            </div>
          </div>

          {available > 0 ? (
            <div className="oc-fin-impact" style={{ marginTop: 8 }}>
              <div className="oc-fin-impact-meta">
                <span className="oc-fin-impact-label">
                  <Landmark size={11} />
                  Repayment impact
                </span>
                <strong className="tabular-nums">
                  {Math.min(100, Math.round((remaining / available) * 100))}% of treasury
                </strong>
              </div>
              <div className="oc-fin-impact-track" aria-hidden>
                <span
                  className="oc-fin-impact-fill"
                  style={{ width: `${Math.min(100, (remaining / available) * 100)}%` }}
                />
              </div>
              <span className="oc-fin-impact-hint">
                {payAmount >= remaining
                  ? `Clearing the loan now takes ${formatCash(remaining, { compact: true })} from your ${formatCash(available, { compact: true })} available balance.`
                  : `Available balance covers ${formatCash(payAmount, { compact: true })} of the ${formatCash(remaining, { compact: true })} outstanding — the rest sweeps from future profits.`}
              </span>
            </div>
          ) : (
            <p className="oc-loan-fine">
              No withdrawable balance right now — repayment continues from house profits and
              the daily sweep.
            </p>
          )}

          {isCreator ? (
            <button
              type="button"
              className="oc-loan-primary"
              disabled={payAmount <= 0 || houseLoanPaying}
              onClick={() =>
                setConfirmModal({
                  type: "pay",
                  title: "Pay down loan",
                  body: `${formatCash(payAmount, { compact: true })} comes from the house balance.`,
                })
              }
            >
              {houseLoanPaying ? (
                <Loader2 size={16} className="oc-treasury-modal-spin" />
              ) : payAmount > 0 ? (
                `Pay ${formatCash(payAmount, { compact: true })}`
              ) : (
                "No balance to pay"
              )}
            </button>
          ) : null}
        </div>
      ) : null}

      {canBorrow ? (
        <div className="oc-loan-card">
          <div className="oc-fin-loan-duo">
            <LoanCreditGauge
              fraction={band ? Number(band.max) / PLATFORM_MAX_PERCENT : 0}
              tone="ok"
              value={maxCash}
              label="Borrowing power"
              minLabel={band ? `${band.min}% min` : ""}
              maxLabel={band ? `${band.max}% max` : ""}
              foot={`${tierLabel(eligibility?.tier)} · backed by ${formatCash(available, {
                compact: true,
              })} treasury`}
            />

            <div>
              <div className="oc-loan-eligible">
                <CheckCircle2 size={16} />
                <div>
                  <strong>Ready to borrow</strong>
                  <p>
                    {tierLabel(eligibility?.tier)} · borrow {band?.min}–{band?.max}% of treasury · up to {maxCash}
                  </p>
                </div>
              </div>
              <ul className="oc-fin-req-list" style={{ marginTop: 8 }}>
                <li className="oc-fin-req-row oc-fin-req-pass">
                  <CheckCircle2 size={13} />
                  Every eligibility check passed — partners active, no open loan, treasury funded.
                </li>
                <li className="oc-fin-req-row oc-fin-req-pass">
                  <CheckCircle2 size={13} />
                  2.5% flat fee, repaid from profits first; withdrawals pause until cleared.
                </li>
              </ul>
            </div>
          </div>

          <p className="oc-loan-section-label">Loan amount</p>
          <p className="oc-loan-amount tabular-nums">
            {loanAmount > 0 ? formatCash(loanAmount, { compact: true }) : maxCash}
          </p>
          <p className="oc-loan-sub">
            {loanAmount > 0
              ? `${numericPercent}% of ${formatCash(available, { compact: true })} treasury balance`
              : `Select a percentage below · up to ${maxCash}`}
          </p>

          <div className="oc-loan-percent-grid">
            {percentPicks.map((pick) => {
              const isActive = percentOk && round2(numericPercent) === pick;
              const isMax = band && pick === round2(Number(band.max));
              return (
                <button
                  key={pick}
                  type="button"
                  className={`oc-loan-percent ${isActive ? "oc-loan-percent-active" : ""} ${isMax ? "oc-loan-percent-max" : ""}`}
                  disabled={!isCreator}
                  onClick={() => setPercent(String(pick))}
                >
                  <strong>{pick}%</strong>
                  <span>{formatCash(round2((available * pick) / 100), { compact: true })}</span>
                  {isMax ? <em>Max</em> : null}
                </button>
              );
            })}
          </div>

          {loanAmount > 0 ? (
            <div className="oc-loan-summary">
              <div>
                <span>You receive</span>
                <strong className="tabular-nums">{formatCash(loanAmount, { compact: true })}</strong>
              </div>
              <div>
                <span>Fee (2.5%)</span>
                <strong className="tabular-nums">{formatCash(loanFee, { compact: true })}</strong>
              </div>
              <div className="oc-loan-summary-total">
                <span>Total to repay</span>
                <strong className="tabular-nums">{formatCash(totalToPay, { compact: true })}</strong>
              </div>
            </div>
          ) : null}

          {isCreator ? (
            <button
              type="button"
              className="oc-loan-primary"
              disabled={!canRequest}
              onClick={() =>
                setConfirmModal({
                  type: "borrow",
                  title: "Confirm borrow",
                  body: `Receive ${formatCash(loanAmount, { compact: true })} · repay ${formatCash(totalToPay, { compact: true })} (incl. fee).`,
                })
              }
            >
              {houseLoanRequesting ? (
                <Loader2 size={16} className="oc-treasury-modal-spin" />
              ) : loanAmount > 0 ? (
                `Borrow ${formatCash(loanAmount, { compact: true })}`
              ) : (
                "Select a percentage"
              )}
            </button>
          ) : null}
        </div>
      ) : null}

      {isLocked ? (
        <div className="oc-loan-card">
          <div className="oc-fin-loan-duo">
            <LoanCreditGauge
              fraction={0}
              tone="locked"
              value="0"
              label="Borrowing power"
              minLabel="1.5% min"
              maxLabel={`${PLATFORM_MAX_PERCENT}% max`}
              foot={
                blockers.length
                  ? `${blockers.length} requirement${blockers.length === 1 ? "" : "s"} blocking your credit line`
                  : "Credit line locked right now"
              }
            />

            {blockers.length > 0 ? (
              <div className="oc-loan-blockers">
                <header>
                  <AlertCircle size={14} />
                  <div>
                    <em>Requirements</em>
                    <strong>Why borrowing is blocked</strong>
                  </div>
                </header>
                <ul className="oc-fin-req-list">
                  {blockers.map((msg, i) => (
                    <li key={i} className="oc-fin-req-row oc-fin-req-fail">
                      <XCircle size={13} />
                      {loanUiCopy(msg)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          {partners.length > 0 ? (
            <ul className="oc-loan-partners">
              {partners.map((member) => {
                const pts = Math.round(member.weeklyScores || 0);
                const ready = Boolean(member.eligible);
                const shortfall = Math.max(0, SCORE_TARGET - pts);
                return (
                  <li key={String(member.userId)}>
                    <span className="oc-loan-partner-avatar">{partnerInitials(member.name)}</span>
                    <div className="oc-loan-partner-copy">
                      <strong>
                        {member.name || "Partner"}
                        <em>{member.role === "owner" ? "Creator" : "Partner"}</em>
                      </strong>
                      <span>
                        {ready
                          ? `${pts} weekly pts · qualifies`
                          : `${pts} weekly pts · needs ${shortfall} more`}
                      </span>
                    </div>
                    {ready ? (
                      <CheckCircle2 size={14} className="oc-loan-partner-pass" />
                    ) : (
                      <XCircle size={14} className="oc-loan-partner-fail" />
                    )}
                  </li>
                );
              })}
            </ul>
          ) : null}

        </div>
      ) : null}

      {loanState?.history?.length > 0 ? (
        <>
          <h3 className="oc-subhead">Loan history</h3>
          <ul className="oc-loan-history">
            {loanState.history.slice(0, 8).map((row) => (
              <li key={row._id}>
                <div>
                  <strong className="tabular-nums">
                    {formatCash(row.loanAmount, { compact: true })}
                  </strong>
                  <span>{row.status || "—"}</span>
                </div>
                <span>{row.issuedAt ? formatAppDate(row.issuedAt) : "—"}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <LoanInfoModal open={infoOpen} onClose={() => setInfoOpen(false)} />
      <ConfirmModal
        open={confirmModal != null}
        title={confirmModal?.title}
        body={confirmModal?.body}
        confirmLabel={confirmModal?.type === "pay" ? "Pay" : "Borrow"}
        busy={confirmModal?.type === "pay" ? houseLoanPaying : houseLoanRequesting}
        termsNote={
          confirmModal?.type === "borrow"
            ? "By requesting a loan, you agree to the house loan terms."
            : undefined
        }
        onClose={() => !houseLoanPaying && !houseLoanRequesting && setConfirmModal(null)}
        onConfirm={() => (confirmModal?.type === "pay" ? runPay() : runRequest())}
      />
    </section>
  );
};

export default OperatorLoanPanel;
